import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import VIPDashboard from "./pages/VIPDashboard";
import { ThemeProvider } from "./contexts/ThemeContext";
import AdminGuard from "./components/AdminGuard";
import VIPGuard from "./components/VIPGuard";
import OperatorGuard from "./components/OperatorGuard";
import ClerkSessionBridge from "./components/ClerkSessionBridge";

// ─── Eagerly loaded (404) ────────────────────────────────────────────────────
import NotFound from "@/pages/NotFound";

// ─── Lazy loaded — Marketing Engine (Public) ─────────────────────────────────

// ─── Lazy loaded — Authentication (Public) ──────────────────────────────────
const SsoCallback = lazy(() => import("./pages/SsoCallback"));

// ─── Lazy loaded — Member Experience (Auth Required) ─────────────────────────
const Onboarding = lazy(() => import("./pages/Onboarding"));

// ─── Lazy loaded — CEP Command (member access required) ──────────────────────
// VIPDashboard eagerly imported above (non-lazy) to fix React context isolation bug
// Elite Command is the Wave 1 home (left-rail CRM shell)
const EliteCommand = lazy(() => import("./pages/EliteCommand"));
const EliteSmartList = lazy(() => import("./pages/EliteSmartList"));
const EliteToolsStub = lazy(() => import("./pages/EliteToolsStub"));
const EliteActivity = lazy(() => import("./pages/EliteActivity"));
const EliteContacts = lazy(() => import("./pages/EliteContacts"));
const ElitePipeline = lazy(() => import("./pages/ElitePipeline"));
const EliteDuplicates = lazy(() => import("./pages/EliteDuplicates"));
const EliteDealEngine = lazy(() => import("./pages/EliteDealEngine"));
const EliteCompWizard = lazy(() => import("./pages/EliteCompWizard"));
const EliteIntelligence = lazy(() => import("./pages/EliteIntelligence"));
const VIPDealTracker = lazy(() => import("./pages/VIPDealTracker"));
const VIPTraining = lazy(() => import("./pages/VIPTraining"));
const VIPResources = lazy(() => import("./pages/VIPResources"));
const VIPAccount = lazy(() => import("./pages/VIPAccount"));
const VIPGadgets = lazy(() => import("./pages/VIPGadgets"));
const VIPGadgetC1C6 = lazy(() => import("./pages/VIPGadgetC1C6"));
const VIPGadgetRelo = lazy(() => import("./pages/VIPGadgetRelo"));
const VIPGadgetDTI = lazy(() => import("./pages/VIPGadgetDTI"));
const VIPGadgetFlip = lazy(() => import("./pages/VIPGadgetFlip"));
const VIPGadgetOptionator = lazy(() => import("./pages/VIPGadgetOptionator"));
const VIPGadgetWholesale = lazy(() => import("./pages/VIPGadgetWholesale"));
const VIPGadgetComps = lazy(() => import("./pages/VIPGadgetComps"));
const VIPGadgetHardship = lazy(() => import("./pages/VIPGadgetHardship"));
const VIPGadgetAppt = lazy(() => import("./pages/VIPGadgetAppt"));
const VIPGadgetStages = lazy(() => import("./pages/VIPGadgetStages"));
const VIPGadgetDiagnosis = lazy(() => import("./pages/VIPGadgetDiagnosis"));
const AdminVIP = lazy(() => import("./pages/AdminVIP"));
const TrainingModule = lazy(() => import("./pages/TrainingModule"));

// ─── Lazy loaded — Op 1 Sniper Framework ─────────────────────────────────────
const Op1CommandCenter = lazy(() => import("./pages/Op1CommandCenter"));
const Op1Pipeline = lazy(() => import("./pages/Op1Pipeline"));
const Op1FollowUpEngine = lazy(() => import("./pages/Op1FollowUpEngine"));
const Op1DecisionRoom = lazy(() => import("./pages/Op1DecisionRoom"));
const Op1DisciplineMonitor = lazy(() => import("./pages/Op1DisciplineMonitor"));

// ─── Lazy loaded — Op 2 VA Manager Framework ─────────────────────────────────
const Op2CommandCenter = lazy(() => import("./pages/Op2CommandCenter"));
const Op2VARoster = lazy(() => import("./pages/Op2VARoster"));
const Op2HandoffQueue = lazy(() => import("./pages/Op2HandoffQueue"));
const Op2DecisionRoom = lazy(() => import("./pages/Op2DecisionRoom"));
const Op2DisciplineMonitor = lazy(() => import("./pages/Op2DisciplineMonitor"));

// ─── Lazy loaded — Op 3 Inbound Marketer Framework ───────────────────────────
const Op3CommandCenter = lazy(() => import("./pages/Op3CommandCenter"));
const Op3LeadIntake = lazy(() => import("./pages/Op3LeadIntake"));
const Op3QualificationEngine = lazy(() => import("./pages/Op3QualificationEngine"));
const Op3CampaignTracker = lazy(() => import("./pages/Op3CampaignTracker"));
const Op3ConversionFunnel = lazy(() => import("./pages/Op3ConversionFunnel"));
const Op3DisciplineMonitor = lazy(() => import("./pages/Op3DisciplineMonitor"));

// ─── Lazy loaded — Admin Panel ────────────────────────────────────────────────
const BuyerCRM = lazy(() => import("./pages/BuyerCRM"));

// ─── Page-level loading skeleton (green on white) ────────────────────────────
function PageLoader() {
  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <div
          style={{
            width: "48px",
            height: "48px",
            border: "3px solid #D9E2DC",
            borderTop: "3px solid #1E4D2B",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
            margin: "0 auto 16px",
          }}
        />
        <div
          style={{
            fontFamily: "Montserrat, sans-serif",
            fontSize: "11px",
            letterSpacing: "0.2em",
            color: "#1E4D2B",
            fontWeight: 700,
          }}
        >
          LOADING
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function Router() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        {/* ─── Root: the app opens on CEP Command ──────────────────────────── */}
        <Route path="/">{() => <Redirect to="/command" />}</Route>
        <Route path="/sso-callback" component={SsoCallback} />

        {/* ─── Onboarding — Auth Required (post-purchase) ──────────────────── */}
        <Route path="/onboarding">
          {() => <VIPGuard skipOperatorRedirect><Onboarding /></VIPGuard>}
        </Route>

        {/* ─── CEP Command — member access required ────────────────────────── */}
        {/* skipOperatorRedirect=true on /command and /command/dashboard to prevent infinite loops */}
        <Route path="/command">
          {() => <VIPGuard skipOperatorRedirect><EliteCommand /></VIPGuard>}
        </Route>
        <Route path="/command/dashboard">
          {() => <VIPGuard skipOperatorRedirect><EliteCommand /></VIPGuard>}
        </Route>
        <Route path="/command/command">
          {() => <VIPGuard skipOperatorRedirect><EliteCommand /></VIPGuard>}
        </Route>
        <Route path="/command/classic">
          {() => <VIPGuard skipOperatorRedirect><VIPDashboard /></VIPGuard>}
        </Route>
        <Route path="/command/smart-lists/:list">
          {() => <VIPGuard><EliteSmartList /></VIPGuard>}
        </Route>
        <Route path="/command/tools/:tool">
          {() => <VIPGuard><EliteToolsStub /></VIPGuard>}
        </Route>
        <Route path="/command/activity">
          {() => <VIPGuard><EliteActivity /></VIPGuard>}
        </Route>
        <Route path="/command/contacts">
          {() => <VIPGuard><EliteContacts /></VIPGuard>}
        </Route>
        <Route path="/command/pipeline">
          {() => <VIPGuard><ElitePipeline /></VIPGuard>}
        </Route>
        <Route path="/command/duplicates">
          {() => <VIPGuard><EliteDuplicates /></VIPGuard>}
        </Route>
        <Route path="/command/deal-engine">
          {() => <VIPGuard><EliteDealEngine /></VIPGuard>}
        </Route>
        <Route path="/command/comp">
          {() => <VIPGuard><EliteCompWizard /></VIPGuard>}
        </Route>
        <Route path="/command/intelligence">
          {() => <VIPGuard><EliteIntelligence /></VIPGuard>}
        </Route>
        <Route path="/command/deals">
          {() => <VIPGuard><VIPDealTracker /></VIPGuard>}
        </Route>
        {/* Aliases — finish brief + old Manus paths */}
        <Route path="/command/deal-tracker">
          {() => <VIPGuard><VIPDealTracker /></VIPGuard>}
        </Route>
        {/* OP2/OP3 aliases from finish brief URLs */}
        <Route path="/command/op2/handoff-queue">
          {() => <VIPGuard><OperatorGuard requiredMode="op2"><Op2HandoffQueue /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op2/decision-room">
          {() => <VIPGuard><OperatorGuard requiredMode="op2"><Op2DecisionRoom /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op3/conversion">
          {() => <VIPGuard><OperatorGuard requiredMode="op3"><Op3ConversionFunnel /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/training">
          {() => <VIPGuard><VIPTraining /></VIPGuard>}
        </Route>
        <Route path="/command/training/:moduleId">
          {() => <VIPGuard><TrainingModule /></VIPGuard>}
        </Route>
        <Route path="/command/resources">
          {() => <VIPGuard><VIPResources /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets">
          {() => <VIPGuard><VIPGadgets /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/c1c6">
          {() => <VIPGuard><VIPGadgetC1C6 /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/relo">
          {() => <VIPGuard><VIPGadgetRelo /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/dti">
          {() => <VIPGuard><VIPGadgetDTI /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/flip">
          {() => <VIPGuard><VIPGadgetFlip /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/optionator">
          {() => <VIPGuard><VIPGadgetOptionator /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/wholesale">
          {() => <VIPGuard><VIPGadgetWholesale /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/comps">
          {() => <VIPGuard><VIPGadgetComps /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/hardship">
          {() => <VIPGuard><VIPGadgetHardship /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/appt">
          {() => <VIPGuard><VIPGadgetAppt /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/stages">
          {() => <VIPGuard><VIPGadgetStages /></VIPGuard>}
        </Route>
        <Route path="/command/gadgets/diagnosis">
          {() => <VIPGuard><VIPGadgetDiagnosis /></VIPGuard>}
        </Route>
        <Route path="/command/account">
          {() => <VIPGuard><VIPAccount /></VIPGuard>}
        </Route>

        {/* ─── Op 1 Sniper Framework — VIP + Op1 Mode Required ────────────── */}
        <Route path="/command/op1">
          {() => <VIPGuard><OperatorGuard requiredMode="op1"><Op1CommandCenter /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op1/pipeline">
          {() => <VIPGuard><OperatorGuard requiredMode="op1"><Op1Pipeline /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op1/followup">
          {() => <VIPGuard><OperatorGuard requiredMode="op1"><Op1FollowUpEngine /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op1/decisions">
          {() => <VIPGuard><OperatorGuard requiredMode="op1"><Op1DecisionRoom /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op1/discipline">
          {() => <VIPGuard><OperatorGuard requiredMode="op1"><Op1DisciplineMonitor /></OperatorGuard></VIPGuard>}
        </Route>

        {/* ─── Op 2 VA Manager Framework — VIP + Op2 Mode Required ────────── */}
        <Route path="/command/op2">
          {() => <VIPGuard><OperatorGuard requiredMode="op2"><Op2CommandCenter /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op2/va-roster">
          {() => <VIPGuard><OperatorGuard requiredMode="op2"><Op2VARoster /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op2/handoff">
          {() => <VIPGuard><OperatorGuard requiredMode="op2"><Op2HandoffQueue /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op2/decisions">
          {() => <VIPGuard><OperatorGuard requiredMode="op2"><Op2DecisionRoom /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op2/discipline">
          {() => <VIPGuard><OperatorGuard requiredMode="op2"><Op2DisciplineMonitor /></OperatorGuard></VIPGuard>}
        </Route>

        {/* ─── Op 3 Inbound Marketer Framework — VIP + Op3 Mode Required ───── */}
        <Route path="/command/op3">
          {() => <VIPGuard><OperatorGuard requiredMode="op3"><Op3CommandCenter /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op3/lead-intake">
          {() => <VIPGuard><OperatorGuard requiredMode="op3"><Op3LeadIntake /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op3/qualification">
          {() => <VIPGuard><OperatorGuard requiredMode="op3"><Op3QualificationEngine /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op3/campaigns">
          {() => <VIPGuard><OperatorGuard requiredMode="op3"><Op3CampaignTracker /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op3/funnel">
          {() => <VIPGuard><OperatorGuard requiredMode="op3"><Op3ConversionFunnel /></OperatorGuard></VIPGuard>}
        </Route>
        <Route path="/command/op3/discipline">
          {() => <VIPGuard><OperatorGuard requiredMode="op3"><Op3DisciplineMonitor /></OperatorGuard></VIPGuard>}
        </Route>

        {/* ─── Admin Panel — Admin Role Required ───────────────────────────── */}
        <Route path="/admin/vip">
          {() => <AdminGuard><AdminVIP /></AdminGuard>}
        </Route>
        <Route path="/admin/crm">
          {() => <AdminGuard><BuyerCRM /></AdminGuard>}
        </Route>

        {/* ─── 404 & Fallback ──────────────────────────────────────────────── */}
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

// Light theme everywhere (green and white).
function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <ClerkSessionBridge />
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
